import getAccountSourceListStore from '../store/accountSourceListStore';
import { AccountSource, accountSourceDataTypeChecker } from '../store/schema/AccountSourceList';

export interface AccountAndHostingSourceId {
    source: AccountSource;
    hostingSourceId?: string;
}

/**
 * Searches through the source list looking for an account for which isAccount returns true
 * @param isAccount determines if the specified account is the one to be located
 * @returns Information about the account and hosting sourceId or undefined if there are not accounts for
 *          which isAccount returns true (expected when the source has been removed)
 */
export function findAccountAndHostingSourceId(
    isAccount: (accountSource: AccountSource) => boolean
): AccountAndHostingSourceId | undefined {
    const accounts: AccountAndHostingSourceId[] = [];

    const sources = getAccountSourceListStore().sources;
    sources.forEach(accountSource => {
        if (isAccount(accountSource)) {
            accounts.push({ source: accountSource });
        } else if (accountSourceDataTypeChecker.isM365UserMailbox(accountSource)) {
            accountSource.sharedSources.sources.forEach(sharedSource => {
                if (isAccount(sharedSource)) {
                    accounts.push({
                        source: sharedSource,
                        hostingSourceId: accountSource.sourceId,
                    });
                }
            });

            accountSource.groupSources.sources.forEach(group => {
                if (isAccount(group)) {
                    accounts.push({
                        source: group,
                        hostingSourceId: accountSource.sourceId,
                    });
                }
            });

            if (
                accountSource.archiveSources.sources.length === 1 &&
                isAccount(accountSource.archiveSources.sources[0])
            ) {
                accounts.push({
                    source: accountSource.archiveSources.sources[0],
                    hostingSourceId: accountSource.sourceId,
                });
            }
        }
    });

    if (accounts.length > 1) {
        throw new Error('Duplicate account found');
    }

    return accounts[0];
}

/**
 * Returns the AccountSource and hosting sourceId associated with the specified sourceId
 * @param sourceId The sourceId to be located
 * @returns Information about the account and hosting sourceId or undefined if the sourceId is
 *          not found in the store (expected when the source has been removed)
 */
export function getAccountAndHostingSourceId(
    sourceId: string
): AccountAndHostingSourceId | undefined {
    return findAccountAndHostingSourceId((accountSource: AccountSource) => {
        return accountSource.sourceId === sourceId;
    });
}

/**
 * Finds the account associated with the specified sourceId
 * @param sourceId Specifies the sourceId to be looked up
 * @returns The specified account
 */
export default function getAccountBySourceId(sourceId: string): AccountSource | undefined {
    return getAccountAndHostingSourceId(sourceId)?.source;
}
